import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useState
} from "react";

import { Notification } from "@magnetic/notification";
import { Status } from "@magnetic/types";

import { VFlex } from "src/components/vFlex";

import "./useToasts.scss";

type ToastStatus = Status | "informational";

interface ToastType {
  id?: number;
  title?: string;
  message: string;
  status?: ToastStatus;
  duration?: number;
}

interface ToastContextType {
  addToast: (toast: ToastType) => void;
  removeToast: (id: number) => void;
  clearAllToasts: () => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

export const ToastProvider = (props: PropsWithChildren) => {
  const [toasts, setToasts] = useState<(ToastType & { id: number })[]>([]);

  const removeToast = useCallback(
    (id: number) => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
    },
    [setToasts]
  );

  const addToast = useCallback(
    (toast: ToastType) => {
      const id = toast.id || Date.now();
      const { title, message, status, duration = 10000 } = toast;

      setToasts((prev) => [...prev, { id, title, message, status, duration }]);

      if (duration > 0) {
        setTimeout(() => {
          removeToast(id);
        }, duration);
      }
    },
    [setToasts, removeToast]
  );

  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, [setToasts]);

  const handleClose = useCallback(
    (id: number) => {
      removeToast(id);
    },
    [removeToast]
  );

  return (
    <ToastContext.Provider value={{ addToast, removeToast, clearAllToasts }}>
      <div className="toast-provider-container">
        {props.children}
        <VFlex className="toasts-container">
          {toasts.map(({ id, title, message, status }) => (
            <Notification
              key={id}
              status={status}
              onClose={() => handleClose(id)}
            >
              {title && <div className="toast-title">{title}</div>}
              <div>{message}</div>
            </Notification>
          ))}
        </VFlex>
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};
